import { useCallback, useEffect, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { FormsElementTypeEnum } from 'generated/resources/graphql'

import { elementFromData } from '../hooks/useForms'
import { Element } from '../lib/Element'
import { Form } from '../lib/Form'
import { SectionElement } from '../lib/elements/SectionElement'
import { HeaderSidebarDropdown } from './dropdowns'
import SectionPlaceHolder from './sidebar/SectionPlaceHolder'
import SidebarElement from './sidebar/SidebarElement'

interface SidebarFormProps {
  form: Form
  resourceId: string
  resourceType: string
  onFormUpdated?: () => void
  selectedElementId: string | null
  onSelectElement?: (element: Element) => void
  onAddElement: (type: FormsElementTypeEnum, sectionId: string | null) => void
  onDuplicateElement: (element: Element) => void
}

type listType = (Element | SectionElement)[]

const cloneElement = (el: Element): Element => {
  return elementFromData({
    id: el.id,
    description: el.description,
    label: el.label,
    mandatory: el.mandatory,
    options: el.options,
    params: JSON.stringify(el.params),
    position: el.position,
    sectionId: el.sectionId || undefined,
    type: el.getType(),
  })
}

const cloneFormElements = (form: Form): listType => {
  // create a copy of the form elements to display and reorder without touching
  // the original form until the drop
  const newElements: Element[] = []
  form.elements.forEach((el: Element) => {
    if (el instanceof SectionElement) {
      const section = new SectionElement(
        el.id,
        el.label,
        el.description,
        el.position,
        el.mandatory,
        el.params,
        el.options,
        el.sectionId
      )
      el.elements.forEach((child: Element) => {
        section.addElement(cloneElement(child))
      })
      newElements.push(section)
    } else {
      newElements.push(cloneElement(el))
    }
  })
  return newElements
}

const findAndRemoveElement = (
  items: listType,
  id: string
): Element | SectionElement | undefined => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i]

    // element is in the form
    if (item && item.id === id) {
      //remove element
      const element = items.splice(i, 1)[0]
      //update all elements position
      items.forEach((el, index) => {
        el.position = index
      })
      return element
    }

    // element inside a section
    if (item instanceof SectionElement) {
      for (let j = 0; j < item.elements.length; j++) {
        const child = item.elements[j]
        if (child && child.id === id) {
          //remove element from section
          const element = item.elements.splice(j, 1)[0]

          //update positions of all elements in the section
          item.elements.forEach((el, index) => {
            el.position = index
          })
          return element
        }
      }
    }
  }
  return undefined
}

const SidebarForm = ({
  form,
  resourceId,
  resourceType,
  onFormUpdated,
  selectedElementId,
  onSelectElement,
  onAddElement,
  onDuplicateElement,
}: SidebarFormProps) => {
  const [elements, setElements] = useState<listType>(() => {
    return cloneFormElements(form)
  })

  //if form elements has been updated, clone again
  useEffect(() => {
    setElements(cloneFormElements(form))
  }, [form, form.elements])

  const moveElement = useCallback(
    (elementId: string, toSectionId: string | null, toIndex: number) => {
      setElements((prevElements: listType) => {
        // this function will take care of 'moving' the dragged element from old
        // position to the new position in the cloned list of elements only to preview
        // source and destination positions can be in the form (first level, form.elements)
        // or a section (second level, section.elements)

        const draggedEl = findAndRemoveElement(prevElements, elementId)
        if (!draggedEl) throw new Error(`Element id ${elementId} not found`)

        const toSection = toSectionId ? prevElements.find((el) => el.id === toSectionId) : undefined
        const newElementList =
          toSection instanceof SectionElement ? toSection.elements : prevElements
        draggedEl.sectionId = toSectionId
        //insert element in correct position
        newElementList.splice(toIndex, 0, draggedEl)
        //update positions (is useless, because position is not used here, we are working with the elements copy)
        newElementList.forEach((element, index) => {
          element.position = index
        })

        return [...prevElements]
      })
    },
    [elements, resourceId, resourceType, form.id]
  )

  const updateElementPosition = useCallback(
    (elementId: string, toPosition: number, toSectionId: string | null) => {
      // this function will update the form once the dragging element is dropped
      // we need to move the element, update all elements position and notify the form
      // has been changed to update the backend
      const draggedEl = findAndRemoveElement(form.elements, elementId)
      if (!draggedEl) throw new Error(`Element id ${elementId} not found`)

      const toSection = toSectionId ? form.elements.find((el) => el.id === toSectionId) : undefined
      const newElementList =
        toSection instanceof SectionElement ? toSection.elements : form.elements
      draggedEl.sectionId = toSectionId
      newElementList.splice(toPosition, 0, draggedEl)
      newElementList.forEach((element, index) => {
        element.position = index
      })

      onFormUpdated?.()
    },
    [form, form.elements, resourceId, resourceType, form.id]
  )

  const selectElement = (elementId: string) => {
    const element = form.getElement(elementId)
    if (element && onSelectElement) {
      onSelectElement(element)
    }
  }

  return (
    <>
      <Box paddingBottom="s16">
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" padding="s16">
          <Text color="textSecondary" size="300" weight="medium">
            {'Questions'}
          </Text>
          <HeaderSidebarDropdown onAddElement={onAddElement} />
        </Box>
        <Divider />
      </Box>
      <DndProvider backend={HTML5Backend}>
        <Box flexDirection="column">
          {elements.map((el: Element, index) => {
            return (
              <SidebarElement
                key={el.id}
                element={el}
                index={index}
                selected={selectedElementId === el.id}
                onSelect={() => selectElement(el.id)}
                form={form}
                onHover={moveElement}
                onDrop={updateElementPosition}
                onAddElement={onAddElement}
                onDuplicateElement={onDuplicateElement}
              >
                {el instanceof SectionElement && (
                  <>
                    <Box style={{ paddingLeft: '1rem' }}>
                      {el.elements.map((child, childIndex) => (
                        <SidebarElement
                          key={child.id}
                          element={child}
                          index={childIndex}
                          selected={selectedElementId === child.id}
                          onSelect={() => selectElement(child.id)}
                          form={form}
                          onHover={moveElement}
                          onDrop={updateElementPosition}
                          onAddElement={onAddElement}
                          onDuplicateElement={onDuplicateElement}
                        />
                      ))}
                      {!el.isLocked() && (
                        <SectionPlaceHolder
                          index={el.elements.length}
                          sectionId={el.id}
                          onHover={moveElement}
                          onDrop={updateElementPosition}
                        />
                      )}
                    </Box>
                  </>
                )}
              </SidebarElement>
            )
          })}
          <SectionPlaceHolder
            index={elements.length}
            sectionId={null}
            onHover={moveElement}
            onDrop={updateElementPosition}
          />
        </Box>
      </DndProvider>
    </>
  )
}

export default SidebarForm
